// components/CalculationSheet/CalculationSheet.tsx
import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Button,
    Divider,
    Grid,
    Chip,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';

import PrintIcon from '@mui/icons-material/Print';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import GroupIcon from '@mui/icons-material/Group';
import { CalculationSheetData } from '../../services/calculationSheet';

interface CalculationSheetProps {
    data: CalculationSheetData;
    onPrint: () => void;
    onBack: () => void;
}

export const CalculationSheet: React.FC<CalculationSheetProps> = ({
    data,
    onPrint,
    onBack,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const safeData = {
        ...data,
        estimatedAmount: data.estimatedAmount || 0,
        miscExpenses: data.miscExpenses || 0,
        vendorOrders: data.vendorOrders || 0,
        laborCost: data.laborCost || 0,
        totalManpower: data.totalManpower || 0,
        fuelCost: data.fuelCost || 0,
        fuelPerVehicle: data.fuelPerVehicle || 0,
        totalVehicles: data.totalVehicles || 0,
        totalVehicleCost: data.totalVehicleCost || 0,
        totalMileage: data.totalMileage || 0,
        tollFees: data.tollFees || 0,
        totalProfit: data.totalProfit || 0,
        memberData: data.memberData || [],
        nonManagerSupervisors: data.nonManagerSupervisors || [],
        expenseDetails: data.expenseDetails || [],
        vendorDetails: data.vendorDetails || [],
        constructionRemarks: data.constructionRemarks || [],
        reportRemarks: data.reportRemarks || [],
        remarks: data.remarks || ''
    };

    // メンバー名を表示用に整形する関数
    const renderMembers = (membersString: string) => {
        const membersList = membersString.split(/[・,、]/);

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {membersList.map((member, index) => {
                    const trimmedMember = member.trim();
                    if (!trimmedMember) return null;

                    return (
                        <Chip
                            key={index}
                            label={trimmedMember}
                            size="small"
                            variant="outlined"
                            sx={{
                                m: 0.25,
                                fontSize: '0.7rem',
                                height: '20px'
                            }}
                        />
                    );
                })}
            </Box>
        );
    };

    // 担当以外の職長データを抽出する関数
    const getNonManagerSupervisors = () => {
        if (!safeData.memberData || !safeData.siteManager) return [];

        return safeData.memberData.filter(item =>
            item.superviser !== safeData.siteManager
        );
    };

    // 担当以外の職長データ
    const nonManagerSupervisors = getNonManagerSupervisors();

    // モバイル表示
    const renderMobileView = () => {
        return (
            <Box sx={{ mb: 3 }}>
                {/* ヘッダー情報 */}
                <Card elevation={2} sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h5" component="div" gutterBottom>
                            {safeData.title || "計算書"}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            入力日: {safeData.inputDate}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            <Typography variant="body1">
                                {safeData.periodStart} 〜 {safeData.periodEnd}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            <Typography variant="body1">
                                {safeData.companyName} (NO. {safeData.companyIndex})
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            <Typography variant="body1">
                                {safeData.siteName}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', mb: 1.5 }}>
                            <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem', visibility: 'hidden' }} />
                            <Typography variant="body2" color="text.secondary">
                                {safeData.address}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <WorkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            <Typography variant="body1">
                                {safeData.workDetails}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            <Typography variant="body1">
                                現場責任者: {safeData.siteManager}
                            </Typography>
                        </Box>

                        {/* 合計利益 - 修正：現場責任者の下に移動 */}
                        <Card elevation={3} sx={{ mt: 2, bgcolor: '#f5f5f5' }}>
                            <CardContent sx={{ py: 1.5 }}>  {/* パディングを少し小さく */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        合計利益
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {safeData.totalProfit.toLocaleString()} 円
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </CardContent>
                </Card>

                {/* 金額情報 */}
                <Card elevation={2} sx={{ mb: 2, bgcolor: '#f9f9f9' }}>
                    <CardContent>
                        <List disablePadding>
                            <ListItem divider sx={{ py: 1 }}>
                                <ListItemText
                                    primary="見積り金額"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {safeData.estimatedAmount.toLocaleString()} 円
                                </Typography>
                            </ListItem>

                            <ListItem divider sx={{ py: 1 }}>
                                <ListItemText
                                    primary="諸経費（合計）"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <Typography variant="body1">
                                    {safeData.miscExpenses.toLocaleString()} 円
                                </Typography>
                            </ListItem>

                            <ListItem divider sx={{ py: 1 }}>
                                <ListItemText
                                    primary="業者発注分（合計）"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <Typography variant="body1">
                                    {safeData.vendorOrders.toLocaleString()} 円
                                </Typography>
                            </ListItem>

                            <ListItem divider sx={{ py: 1 }}>
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body1" fontWeight={500}>人件費</Typography>
                                        <Typography variant="body1">
                                            {data.laborCost.toLocaleString()} 円
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <GroupIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            総人工: {data.totalManpower.toLocaleString()} 人工
                                        </Typography>
                                    </Box>
                                </Box>
                            </ListItem>

                            <ListItem divider sx={{ py: 1 }}>
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body1" fontWeight={500}>燃料費(往復)</Typography>
                                        <Typography variant="body1">
                                            {data.fuelCost.toLocaleString()} 円
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <LocalGasStationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            総走行距離: {data.totalMileage.toLocaleString()} km
                                        </Typography>
                                    </Box>
                                </Box>
                            </ListItem>

                            <ListItem divider sx={{ py: 1 }}>
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body1" fontWeight={500}>車両費</Typography>
                                        <Typography variant="body1">
                                            {data.totalVehicleCost.toLocaleString()} 円
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <DirectionsCarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            台数: {data.totalVehicles} 台
                                        </Typography>
                                    </Box>
                                </Box>
                            </ListItem>

                            <ListItem sx={{ py: 1 }}>  {/* 最後のアイテムなので divider を削除 */}
                                <ListItemText
                                    primary="高速料金(ETC)"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <Typography variant="body1">
                                    {safeData.tollFees.toLocaleString()} 円
                                </Typography>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>

                {/* メンバー情報 */}
                {safeData.memberData && safeData.memberData.length > 0 && (
                    <Card elevation={2} sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                                メンバー情報
                            </Typography>

                            {safeData.memberData.map((item, index) => (
                                <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < safeData.memberData.length - 1 ? '1px solid #eee' : 'none' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography color="primary" fontWeight={500}>
                                            {item.date}
                                        </Typography>
                                        <Typography>
                                            職長: {item.superviser}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {renderMembers(item.members)}
                                    </Box>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                )}

                {/* 職長手当と備考欄 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    {/* 職長手当 */}
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                                職長手当
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                                <Typography fontWeight={500}>手当額</Typography>
                                <Typography>{safeData.supervisorAllowance?.toLocaleString() || 0}</Typography>
                            </Box>

                            {nonManagerSupervisors.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        担当以外の職長:
                                    </Typography>

                                    {nonManagerSupervisors.map((item, index) => (
                                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2">{item.date}</Typography>
                                            <Typography variant="body2">{item.superviser}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #eee' }}>
                                <Typography fontWeight={500}>職長手当対象人数</Typography>
                                <Typography>{safeData.nonManagerSupervisorCount} 人</Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* 備考欄 */}
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                                備考欄
                            </Typography>

                            {safeData.remarks ? (
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                    {safeData.remarks}
                                </Typography>
                            ) : (
                                <>
                                    {safeData.expenseDetails && safeData.expenseDetails.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【諸経費】</Typography>
                                            {safeData.expenseDetails.map((item, index) => (
                                                <Box key={`expense-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                    ・{item.detail}: {item.price.toLocaleString()}円
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {safeData.vendorDetails && safeData.vendorDetails.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【業者発注分】</Typography>
                                            {safeData.vendorDetails.map((item, index) => (
                                                <Box key={`vendor-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                    ・{item.detail}: {item.price.toLocaleString()}円
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {safeData.constructionRemarks && safeData.constructionRemarks.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【工事備考】</Typography>
                                            {safeData.constructionRemarks.map((item, index) => (
                                                <Box key={`construction-remark-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                    ・{item.remarks}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {safeData.reportRemarks && safeData.reportRemarks.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【作業備考】</Typography>
                                            {safeData.reportRemarks.map((item, index) => (
                                                <Box key={`report-remark-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                    ・{item.date}: {item.remarks}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* ここから合計利益のカードを削除（上に移動済み） */}
            </Box>
        );
    };

    // デスクトップ表示（従来のコード）
    const renderDesktopView = () => (
        <Paper
            elevation={2}
            sx={{
                p: 3,
                maxWidth: '210mm', // A4幅
                margin: '0 auto',
                '@media print': {
                    boxShadow: 'none',
                    p: 0
                }
            }}
            id="print-area"
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">{safeData.title}</Typography>
                <Typography variant="body2">入力日: {safeData.inputDate}</Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, boxShadow: 'none' }}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell component="th" variant="head" sx={{ width: '20%', minWidth: '80px' }}>日付</TableCell>
                            <TableCell>{safeData.periodStart}</TableCell>
                            <TableCell sx={{ width: '5%', px: { xs: 0, sm: 1 } }}>～</TableCell>
                            <TableCell>{safeData.periodEnd}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell component="th" variant="head">会社名</TableCell>
                            <TableCell colSpan={2}>{safeData.companyName}</TableCell>
                            <TableCell>NO. {safeData.companyIndex}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell component="th" variant="head">現場名</TableCell>
                            <TableCell colSpan={4}>{safeData.siteName}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell component="th" variant="head">住所</TableCell>
                            <TableCell colSpan={3} sx={{ wordBreak: 'break-word' }}>{safeData.address}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" variant="head">作業内容</TableCell>
                            <TableCell colSpan={3} sx={{ wordBreak: 'break-word' }}>{safeData.workDetails}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" variant="head">現場責任者</TableCell>
                            <TableCell colSpan={3}>{safeData.siteManager}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, boxShadow: 'none' }}>
                <Table size="small">
                    <TableBody>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head" sx={{ width: '30%', minWidth: '120px' }}>見積り金額</TableCell>
                            <TableCell align="right">{safeData.estimatedAmount.toLocaleString()} 円</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head">諸経費（合計）</TableCell>
                            <TableCell align="right">{safeData.miscExpenses.toLocaleString()} 円</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head">業者発注分（合計）</TableCell>
                            <TableCell align="right">{safeData.vendorOrders.toLocaleString()} 円</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head">人件費</TableCell>
                            <TableCell>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={3} sm={2}>総人工</Grid>
                                    <Grid item xs={2} sx={{ textAlign: 'right' }}>{data.totalManpower.toLocaleString()}</Grid>
                                    <Grid item xs={1}>人工</Grid>
                                    <Grid item xs={2} sm={3} sx={{ textAlign: 'right' }}>合計</Grid>
                                    <Grid item xs={4} sx={{ textAlign: 'right' }}>{safeData.laborCost.toLocaleString()} 円</Grid>
                                </Grid>
                            </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head">燃料費(往復)</TableCell>
                            <TableCell>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={3} sm={2}>総走行距離</Grid>
                                    <Grid item xs={2} sx={{ textAlign: 'right' }}>{data.totalMileage.toLocaleString()}</Grid>
                                    <Grid item xs={1}>km</Grid>
                                    <Grid item xs={2} sm={3} sx={{ textAlign: 'right' }}>合計</Grid>
                                    <Grid item xs={4} sx={{ textAlign: 'right' }}>{data.fuelCost.toLocaleString()} 円</Grid>
                                </Grid>
                            </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head">車両費</TableCell>
                            <TableCell>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={3} sm={2}>台数</Grid>
                                    <Grid item xs={2} sx={{ textAlign: 'right' }}>{data.totalVehicles}</Grid>
                                    <Grid item xs={1}>台</Grid>
                                    <Grid item xs={2} sm={3} sx={{ textAlign: 'right' }}>合計</Grid>
                                    <Grid item xs={4} sx={{ textAlign: 'right' }}>{data.totalVehicleCost.toLocaleString()} 円</Grid>
                                </Grid>
                            </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell component="th" variant="head">高速料金(ETC)</TableCell>
                            <TableCell align="right">{safeData.tollFees.toLocaleString()} 円</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* メンバー情報テーブル */}
            {safeData.memberData && safeData.memberData.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                                <TableCell width="20%" sx={{ minWidth: '80px' }}>日付</TableCell>
                                <TableCell width="20%" sx={{ minWidth: '80px' }}>職長</TableCell>
                                <TableCell width="60%">メンバー</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {safeData.memberData.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>{item.date}</TableCell>
                                    <TableCell>{item.superviser}</TableCell>
                                    <TableCell>{renderMembers(item.members)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* 2つのテーブルを横に並べるコンテナ - モバイルでは縦に */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 2,
                gap: 2
            }}>
                {/* 職長手当テーブル */}
                <TableContainer component={Paper} variant="outlined" sx={{
                    boxShadow: 'none',
                    width: { xs: '100%', sm: '40%' }
                }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '50%', backgroundColor: '#f5f5f5', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                    職長手当
                                </TableCell>
                                <TableCell sx={{ width: '50%', backgroundColor: '#f5f5f5', textAlign: 'center', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                    {safeData.supervisorAllowance?.toLocaleString() || 0}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* 担当以外の職長の手当表示 */}
                            {nonManagerSupervisors.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                                        {item.date}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                        {item.superviser}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {/* 職長手当対象人数 */}
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)', whiteSpace: 'nowrap' }}>
                                    職長手当対象人数
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                    {safeData.nonManagerSupervisorCount} 人
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 備考欄テーブル */}
                <TableContainer component={Paper} variant="outlined" sx={{
                    boxShadow: 'none',
                    width: { xs: '100%', sm: '60%' },
                    ml: { xs: 0, sm: 0 }
                }}>
                    <Table size="small" sx={{ height: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#f5f5f5', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                    備考欄
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ height: '100%' }}>
                                <TableCell
                                    sx={{
                                        border: '1px solid rgba(224, 224, 224, 1)',
                                        height: '100%',
                                        verticalAlign: 'top',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        p: { xs: 1, sm: 2 }
                                    }}
                                >
                                    {/* 備考欄の内容 */}
                                    {safeData.remarks || (
                                        <>
                                            {safeData.expenseDetails && safeData.expenseDetails.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【諸経費】</Typography>
                                                    {safeData.expenseDetails.map((item, index) => (
                                                        <Box key={`expense-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                            ・{item.detail}: {item.price.toLocaleString()}円
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {safeData.vendorDetails && safeData.vendorDetails.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【業者発注分】</Typography>
                                                    {safeData.vendorDetails.map((item, index) => (
                                                        <Box key={`vendor-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                            ・{item.detail}: {item.price.toLocaleString()}円
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {safeData.constructionRemarks && safeData.constructionRemarks.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【工事備考】</Typography>
                                                    {safeData.constructionRemarks.map((item, index) => (
                                                        <Box key={`construction-remark-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                            ・{item.remarks}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {safeData.reportRemarks && safeData.reportRemarks.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>【作業備考】</Typography>
                                                    {safeData.reportRemarks.map((item, index) => (
                                                        <Box key={`report-remark-${index}`} sx={{ ml: 2, mb: 0.5 }}>
                                                            ・{item.date}: {item.remarks}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* 合計利益テーブル */}
            <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', mb: 3 }}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell component="th" variant="head" sx={{ width: '50%', textAlign: 'center', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                合計利益
                            </TableCell>
                            <TableCell sx={{ width: '50%', textAlign: 'right', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                                {safeData.totalProfit.toLocaleString()} 円
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
                <Button variant="outlined" onClick={onBack}>
                    戻る
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={onPrint}
                >
                    印刷
                </Button>
            </Box>

            {isMobile ? renderMobileView() : renderDesktopView()}
        </Box>
    );
};



